import type { KnownMapping, SchemaField } from "@/types";
import { buildApplyMappingsPayload } from "@/lib/applyMappingsPayload";
import { compareRegionIdsByNumber } from "@/lib/mappingRegions";
import { applyMappings, type ApplyMappingsResult } from "@/services/api";

export interface RegionDqxApplyRun {
  regionId: string | null;
  runId: number;
  jobId: string;
  message: string | null;
  rowsWritten: number;
  mappingRunId?: string | null;
}

export interface MultiRegionDqxApplyResult {
  runs: RegionDqxApplyRun[];
  rowsWritten: number;
}

export interface RegionMappingGroup {
  regionId: string | null;
  reportType: string | null;
  mappings: KnownMapping[];
}

/** Group saved mappings by regionId, then order groups by region_group number. */
export function groupMappingsByRegion(savedMappings: KnownMapping[]): RegionMappingGroup[] {
  const groups: RegionMappingGroup[] = [];
  const indexByRegion = new Map<string, number>();

  for (const mapping of savedMappings) {
    const key = mapping.regionId?.trim() || "";
    const existing = indexByRegion.get(key);
    if (existing != null) {
      groups[existing]!.mappings.push(mapping);
      if (!groups[existing]!.reportType && mapping.reportType) {
        groups[existing]!.reportType = mapping.reportType;
      }
      continue;
    }
    indexByRegion.set(key, groups.length);
    groups.push({
      regionId: key || null,
      reportType: mapping.reportType ?? null,
      mappings: [mapping],
    });
  }

  return groups.sort((a, b) => compareRegionIdsByNumber(a.regionId, b.regionId));
}

/**
 * Board Run DQ: one apply + DQX trigger per mapped table/region.
 * Unscoped snapshots (no regionId) keep a single file-wide apply.
 * A single group that still has regionId is scoped — never file-wide.
 */
export async function applyMappingsPerRegionWithDqx(params: {
  fileId: string;
  schemaId: string;
  reportType?: string | null;
  savedMappings: KnownMapping[];
  schemaFields: SchemaField[];
  apply?: typeof applyMappings;
}): Promise<MultiRegionDqxApplyResult> {
  const applyFn = params.apply ?? applyMappings;
  const groups = groupMappingsByRegion(params.savedMappings);
  const hasRegionScope = groups.some((g) => g.regionId != null);

  // No region scope: one apply for the whole file (backend may still filter by selection).
  if (!hasRegionScope) {
    const mappings = buildApplyMappingsPayload(params.savedMappings, params.schemaFields);
    const result = await applyFn({
      fileId: params.fileId,
      schemaId: params.schemaId,
      reportType: params.reportType ?? null,
      mappings,
      multiRegion: false,
      triggerDqx: true,
    });
    return singleRunResult(null, result);
  }

  const runs: RegionDqxApplyRun[] = [];
  let rowsWritten = 0;

  for (let index = 0; index < groups.length; index += 1) {
    const group = groups[index]!;
    if (!group.regionId) {
      throw new Error("Multi-region Run DQ requires regionId on every mapping group.");
    }
    const mappings = buildApplyMappingsPayload(group.mappings, params.schemaFields);
    const result = await applyFn({
      fileId: params.fileId,
      schemaId: params.schemaId,
      reportType: group.reportType ?? params.reportType ?? null,
      mappings,
      regionId: group.regionId,
      append: index > 0,
      multiRegion: true,
      triggerDqx: true,
    });
    const runId = result.dqx_run_id ?? null;
    if (runId == null) {
      throw new Error(
        `Data quality checks could not be started for table "${group.regionId}" (no run id returned).`
      );
    }
    rowsWritten += result.rows_written;
    runs.push({
      regionId: group.regionId,
      runId,
      jobId: result.dqx_job_id ?? "",
      message: result.dqx_trigger_message ?? null,
      rowsWritten: result.rows_written,
    });
  }

  return { runs, rowsWritten };
}

function singleRunResult(
  regionId: string | null,
  result: ApplyMappingsResult
): MultiRegionDqxApplyResult {
  const runId = result.dqx_run_id ?? null;
  if (runId == null) {
    throw new Error("Data quality checks could not be started (no run id returned).");
  }
  return {
    rowsWritten: result.rows_written,
    runs: [
      {
        regionId,
        runId,
        jobId: result.dqx_job_id ?? "",
        message: result.dqx_trigger_message ?? null,
        rowsWritten: result.rows_written,
      },
    ],
  };
}
